import { useCallback, useEffect, useMemo } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TestingAccordion from "../TestingAccordion/TestingAccordion";
import {
  GaitData,
  TestsData,
} from "../../../../../recoil-states/create-order-states";
import { TestType } from "../../../../../enums/Test";
import { ICreateGaitOrderItem, IGaitTestItem } from "../../../../../interfaces/Order";
import { getTestDurationOptions } from "../../../../../helpers/TestHelper";
import { getDefaultTestsData } from "../../../../../helpers/OrderHelper";
import TestFrequencyInputs from "../TestFrequencyInputs/TestFrequencyInputs";
import InputField from "../../../../InputField/InputField";
import Button from "../../../../Buttons/Button/Button";
import { DurationType, FrequencyType } from "../../../../../enums/Order";
import { TestDurationValue } from "../../../../../interfaces/Test";
import { extractDurationAndType } from "../../../../../helpers/OrderHelper";
import styles from "./GaitTest.module.scss";

const GaitTest = () => {
  const { selected, tests } = useRecoilValue(GaitData);
  const setTestsData = useSetRecoilState(TestsData);

  // Validate that ALL tests are complete
  const hasIncompleteTests = useMemo(
    () =>
      tests.some((test) => {
        // Check if test has all required fields
        if (!test.frequency || !test.frequencyReps) return true;

        // For custom duration, check if duration is set
        if ("isCustom" in test && test.isCustom) {
          return !("duration" in test) || !test.duration;
        }

        // For preset duration, check if duration is set
        return !("duration" in test) || !test.duration;
      }),
    [tests]
  );

  const error = useMemo(
    (): boolean => selected && (tests.length === 0 || hasIncompleteTests),
    [selected, tests.length, hasIncompleteTests]
  );

  // Helper functions for individual test manipulation
  const getIsCustomDuration = (test: IGaitTestItem): boolean => {
    return Boolean(test && "isCustom" in test && test.isCustom);
  };

  const getDurationDropdownValue = (test: IGaitTestItem): string => {
    if (!test) return "";
    const isCustom = getIsCustomDuration(test);
    if (isCustom) return "custom";

    // Match against preset durations - check if duration and durationType exist
    if ("duration" in test && "durationType" in test) {
      return `${test.duration}_${test.durationType}`;
    }

    return "";
  };

  const getCustomDurationInputs = (test: IGaitTestItem) => {
    const isCustom = getIsCustomDuration(test);
    if (!isCustom || !test) return { minutes: 0, seconds: 0 };

    // Check if duration exists in current test
    if ("duration" in test && test.duration) {
      const totalSeconds = test.duration;
      const seconds = totalSeconds % 60;
      const minutes = (totalSeconds - seconds) / 60;

      return { minutes, seconds };
    }

    return { minutes: 0, seconds: 0 };
  };

  const handleDataChange = useCallback(
    (value: Partial<ICreateGaitOrderItem>) => {
      setTestsData((prev) => ({
        ...prev,
        [TestType.Gait]: {
          ...prev[TestType.Gait],
          ...value,
          selected: value.selected ?? true, // select test on input change
        },
      }));
    },
    [setTestsData]
  );

  useEffect(() => {
    setTestsData((prev) => ({
      ...prev,
      [TestType.Gait]: { ...prev[TestType.Gait], valid: !error },
    }));
  }, [error, setTestsData]);

  const handleSelectionChange = (value: boolean): void => {
    if (value) {
      // Auto-select default: 10 seconds, 1 time per week
      handleDataChange({
        selected: true,
        tests: [
          {
            duration: 10,
            durationType: DurationType.Seconds,
            frequency: FrequencyType.Weekly,
            frequencyReps: 1,
          },
        ],
      });
    } else {
      handleDataChange(getDefaultTestsData()[TestType.Gait]);
    }
  };

  const handleAddMore = () => {
    handleDataChange({
      tests: [
        ...tests,
        {
          duration: 10,
          durationType: DurationType.Seconds,
          frequency: FrequencyType.Weekly,
          frequencyReps: 1,
        },
      ],
    });
  };

  const handleRemoveTest = (index: number) => {
    if (tests.length <= 1) return; // Prevent removal of last test
    const newTests = tests.filter((_, i) => i !== index);
    handleDataChange({ tests: newTests });
  };

  const handleDurationDropdownChange = (index: number, value: string) => {
    const currentTest = tests[index];
    const newTests = [...tests];

    if (value === "custom") {
      // Switch to custom duration
      newTests[index] = {
        isCustom: true,
        frequency: currentTest?.frequency,
        frequencyReps: currentTest?.frequencyReps,
      };
    } else {
      // Preset duration selected
      const { duration, durationType } = extractDurationAndType(value as TestDurationValue);
      newTests[index] = {
        duration,
        durationType,
        frequency: currentTest?.frequency || FrequencyType.Weekly,
        frequencyReps: currentTest?.frequencyReps || 1,
      };
    }

    handleDataChange({ tests: newTests });
  };

  const handleCustomMinutesChange = (index: number, value: string) => {
    const currentTest = tests[index];
    const customInputs = getCustomDurationInputs(currentTest);
    const minutes = Number(value) || 0;
    const seconds = customInputs.seconds;
    const totalDuration = minutes * 60 + seconds;

    const newTests = [...tests];
    newTests[index] = {
      duration: totalDuration !== 0 ? totalDuration : undefined,
      durationType: DurationType.Seconds,
      isCustom: true,
      frequency: currentTest?.frequency,
      frequencyReps: currentTest?.frequencyReps,
    };

    handleDataChange({ tests: newTests });
  };

  const handleCustomSecondsChange = (index: number, value: string) => {
    const currentTest = tests[index];
    const customInputs = getCustomDurationInputs(currentTest);
    const seconds = Number(value) || 0;
    const minutes = customInputs.minutes;
    const totalDuration = minutes * 60 + seconds;

    const newTests = [...tests];
    newTests[index] = {
      duration: totalDuration !== 0 ? totalDuration : undefined,
      durationType: DurationType.Seconds,
      isCustom: true,
      frequency: currentTest?.frequency,
      frequencyReps: currentTest?.frequencyReps,
    };

    handleDataChange({ tests: newTests });
  };

  const handleFrequencyChange = (index: number, frequencyParams: Partial<IGaitTestItem>) => {
    const newTests = [...tests];
    newTests[index] = {
      ...newTests[index],
      ...frequencyParams,
    } as IGaitTestItem;

    handleDataChange({ tests: newTests });
  };

  return (
    <TestingAccordion
      error={error}
      testName="Gait"
      selected={selected}
      onSelectionChange={handleSelectionChange}
      children={
        <div className="flex flex-col gap-5">
          {/* Render each test row */}
          {tests.map((test, index) => {
            const isCustom = getIsCustomDuration(test);
            const durationValue = getDurationDropdownValue(test);
            const customInputs = getCustomDurationInputs(test);
            const showDurationError =
              selected &&
              isCustom &&
              (!test || !("duration" in test) || !test.duration);

            return (
              <div key={index} className="flex flex-wrap gap-5 items-start">
                {/* Duration Column */}
                <div className="flex flex-col gap-2 w-full md:w-[calc(58%-12.5px)]">
                  {index === 0 && (
                    <label
                      className={`text-base font-medium ${
                        !test ? "text-crimson" : "text-cloudBurst"
                      }`}
                    >
                      Test Duration *
                    </label>
                  )}
                  <div className="flex items-center gap-1">
                    <div
                      className={styles.select_wrapper}
                      style={{ width: "150px", minWidth: "150px" }}
                    >
                      <Select
                        fullWidth
                        MenuProps={{
                          classes: { root: styles.select_root },
                        }}
                        value={durationValue}
                        onChange={({ target: { value } }) =>
                          handleDurationDropdownChange(index, value)
                        }
                        error={!test}
                      >
                        {getTestDurationOptions().map(({ label, value }) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                        <MenuItem value="custom">Custom</MenuItem>
                      </Select>
                    </div>

                    {isCustom && (
                      <div className="flex items-center gap-1 ml-2.5">
                        <InputField
                          isNumericField
                          error={showDurationError}
                          placeholder="0"
                          className="w-12"
                          value={
                            customInputs.minutes
                              ? String(customInputs.minutes)
                              : ""
                          }
                          onChange={({ target: { value } }) =>
                            handleCustomMinutesChange(index, value)
                          }
                        />
                        <span
                          className="text-xs text-cloudBurst whitespace-nowrap"
                          style={{ marginRight: "5px" }}
                        >
                          min.
                        </span>
                        <InputField
                          isNumericField
                          error={showDurationError}
                          placeholder="0"
                          className="w-12"
                          value={
                            customInputs.seconds
                              ? String(customInputs.seconds)
                              : ""
                          }
                          onChange={({ target: { value } }) =>
                            handleCustomSecondsChange(index, value)
                          }
                        />
                        <span className="text-xs text-cloudBurst whitespace-nowrap">
                          sec.
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Frequency Column with Remove Button */}
                <div className="w-full md:w-[calc(42%-12.5px)] flex gap-2">
                  <div className="flex-1">
                    <TestFrequencyInputs
                      isTestSelected={selected}
                      frequency={test?.frequency}
                      frequencyReps={test?.frequencyReps}
                      onChange={(frequencyParams) =>
                        handleFrequencyChange(index, frequencyParams)
                      }
                      hideLabel={index > 0}
                    />
                  </div>

                  {/* Remove Button - only show if more than one test */}
                  {tests.length > 1 && (
                    <div className="flex items-center group" style={{ paddingTop: index === 0 ? "32px" : "0" }}>
                      <button
                        onClick={() => handleRemoveTest(index)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-crimson hover:bg-crimson/10 transition-all duration-200"
                      >
                        <span className="icon_svg icon_cross !bg-crimson w-4 h-4" />
                        <span className="hidden group-hover:inline text-sm font-medium whitespace-nowrap">
                          Delete
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Add More Button */}
          <div className="flex justify-start">
            <Button onClick={handleAddMore} className="!bg-white !text-pelorous border border-pelorous">
              <span className="icon_svg icon_plus !bg-pelorous" />
              Add more
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default GaitTest;
